import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Collapse,
  Flex,
  IconButton,
  Input,
  InputGroup,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { RepeatIcon, SearchIcon, SmallCloseIcon } from '@chakra-ui/icons';

import Card from 'components/card/Card';
import BaraFeatureGuard from 'components/bara/BaraFeatureGuard';
import Paginate from 'components/paginate/Paginate';
import { apiGetBaraAccessibleBranches } from 'api';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import type { APIBaraBranch } from 'types/api.types';

const PAGE_SIZE = 20;

const BaraBranchSettingsListView = () => {
  const isBaraEnabled = useFeatureFlag('enable-bara');
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const inputBg = useColorModeValue('white', 'navy.900');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');

  const [showSearchBar, setShowSearchBar] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [branches, setBranches] = useState<APIBaraBranch[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchBranches = (nameVal: string, pageVal: number) => {
    setLoading(true);
    apiGetBaraAccessibleBranches({
      page: pageVal + 1,
      page_size: PAGE_SIZE,
      name: nameVal || undefined,
    })
      .then((data: any) => {
        setBranches(Array.isArray(data?.results) ? data.results : []);
        setTotal(data?.count ?? 0);
      })
      .catch(() => {
        toastRef.current({
          title: 'Failed to load branches',
          status: 'error',
          duration: 3000,
        });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!isBaraEnabled) return;
    fetchBranches('', 0);
  }, [isBaraEnabled]);

  const handleSearchChange = (val: string) => {
    setSearch(val);
    setPage(0);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => fetchBranches(val, 0), 300);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchBranches(search, newPage);
  };

  const handleReset = () => {
    setSearch('');
    setPage(0);
    fetchBranches('', 0);
  };

  return (
    <BaraFeatureGuard>
      <Card
        flexDirection="column"
        w="100%"
        px="0px"
        overflowX={{ sm: 'scroll', lg: 'hidden' }}
      >
        {/* Header */}
        <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
          <Text
            color={textColor}
            fontSize="22px"
            fontWeight="700"
            lineHeight="100%"
          >
            After-Hours Referral Branch Settings
          </Text>
          <Flex gap={2}>
            <Tooltip
              label={showSearchBar ? 'Hide search' : 'Show search'}
              hasArrow
            >
              <IconButton
                aria-label={showSearchBar ? 'Hide search' : 'Show search'}
                icon={<SearchIcon />}
                variant={showSearchBar ? 'solid' : 'ghost'}
                colorScheme={showSearchBar ? 'yellow' : 'gray'}
                onClick={() => setShowSearchBar(!showSearchBar)}
                size="md"
              />
            </Tooltip>
            <Tooltip label="Refresh" hasArrow>
              <IconButton
                aria-label="Refresh"
                icon={<RepeatIcon />}
                variant="ghost"
                isLoading={loading}
                onClick={() => fetchBranches(search, page)}
              />
            </Tooltip>
          </Flex>
        </Flex>

        {/* Search */}
        <Collapse in={showSearchBar} animateOpacity>
          <Box
            px="20px"
            pb="20px"
            borderBottom="1px solid"
            borderColor={borderColor}
          >
            <Stack
              spacing={4}
              direction={{ base: 'column', lg: 'row' }}
              align="flex-end"
            >
              <Box flex="1">
                <InputGroup>
                  <Input
                    variant="main"
                    placeholder="Search by branch name"
                    value={search}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    bg={inputBg}
                    size="md"
                  />
                </InputGroup>
              </Box>
              <Flex gap={2}>
                <Button
                  onClick={handleReset}
                  variant="outline"
                  leftIcon={<SmallCloseIcon />}
                  size="md"
                >
                  Reset
                </Button>
              </Flex>
            </Stack>
          </Box>
        </Collapse>

        {/* Table */}
        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              <Th pe="10px" borderColor={borderColor} color="gray.400">
                Branch
              </Th>
              <Th borderColor={borderColor} />
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <Tr>
                <Td colSpan={2} textAlign="center">
                  <Flex align="center" justify="center" mt="20px">
                    <Spinner size="sm" mr="10px" /> Loading branches...
                  </Flex>
                </Td>
              </Tr>
            ) : branches.length === 0 ? (
              <Tr>
                <Td colSpan={2} textAlign="center" color="gray.400">
                  No branches found.
                </Td>
              </Tr>
            ) : (
              branches.map((branch) => (
                <Tr
                  key={branch.id}
                  cursor="pointer"
                  _hover={{ bg: hoverBg }}
                  onClick={() =>
                    navigate(
                      `/admin/settings/bara/branch-settings/${branch.id}`,
                    )
                  }
                >
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontSize="sm" fontWeight="700">
                      {branch.name}
                    </Text>
                  </Td>
                  <Td borderColor={borderColor} isNumeric>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="brand"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(
                          `/admin/settings/bara/branch-settings/${branch.id}`,
                        );
                      }}
                    >
                      Configure
                    </Button>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>

        <Paginate
          page={page}
          count={total}
          pageSize={PAGE_SIZE}
          onSetPage={handlePageChange}
        />
      </Card>
    </BaraFeatureGuard>
  );
};

export default BaraBranchSettingsListView;
